import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const ORANGE = '#f57c00';
const ORANGE_LIGHT = '#fff3e0';
const ORANGE_DARK = '#e65100';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({ schedules: 0, shifts: 0, invigilators: 0, remuneration: 0 });
  const [recentSchedules, setRecentSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        axios.get('/schedules'),
        axios.get('/shifts'),
        axios.get('/users'),
        axios.get('/remuneration/summary'),
      ]);

      const schedules   = results[0].status === 'fulfilled' ? results[0].value.data || [] : [];
      const shifts      = results[1].status === 'fulfilled' ? results[1].value.data || [] : [];
      const users       = results[2].status === 'fulfilled' ? results[2].value.data || [] : [];
      const remData     = results[3].status === 'fulfilled' ? results[3].value.data : null;

      const invigilators = users.filter(u => u.role_name === 'invigilator' || u.role === 'invigilator');
      const today = new Date().toISOString().split('T')[0];

      // ✅ FIX: remData is an array — sum total_amount across all rows
      let totalRemuneration = 0;
      if (Array.isArray(remData)) {
        totalRemuneration = remData.reduce((acc, item) => acc + Number(item.total_amount || 0), 0);
      } else if (remData?.total) {
        totalRemuneration = Number(remData.total);
      }

      setStats({
        schedules: schedules.length,
        shifts: shifts.filter(s => s.shift_date >= today).length,
        invigilators: invigilators.length,
        remuneration: totalRemuneration,
      });

      const sorted = [...schedules].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setRecentSchedules(sorted.slice(0, 5));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, [refreshKey]);

  useEffect(() => {
    const interval = setInterval(() => setRefreshKey(prev => prev + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const cards = [
    {
      label: 'Total Schedules',
      value: stats.schedules,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      ),
      path: '/schedules',
      trend: '+12%',
      trendUp: true,
    },
    {
      label: 'Upcoming Shifts',
      value: stats.shifts,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      path: '/shifts',
      trend: 'Active',
      trendUp: true,
    },
    {
      label: 'Invigilators',
      value: stats.invigilators,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      path: '/users',
      trend: 'Registered',
      trendUp: true,
    },
    {
      label: 'Total Remuneration',
      value: `₹${Number(stats.remuneration).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      ),
      path: '/remuneration/summary',
      trend: 'Disbursed',
      trendUp: true,
    },
  ];

  const statusConfig = {
    active:    { bg: '#fff3e0', color: '#e65100', dot: ORANGE },
    completed: { bg: '#e8f5e9', color: '#2e7d32', dot: '#43a047' },
    cancelled: { bg: '#ffebee', color: '#c62828', dot: '#ef5350' },
  };

  return (
    <div style={{ padding: '32px', backgroundColor: '#fafafa', minHeight: '100vh', fontFamily: "'Segoe UI', sans-serif" }}>

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px',
              background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DARK})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px'
            }}>
              {user?.name?.[0]?.toUpperCase() || 'A'}
            </div>
            <div>
              <h2 style={{ color: '#1a1a1a', margin: 0, fontSize: '22px', fontWeight: 700 }}>
                Welcome back, {user?.name}
              </h2>
              <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>
                <span style={{
                  background: ORANGE_LIGHT, color: ORANGE, padding: '2px 8px',
                  borderRadius: '20px', fontSize: '11px', fontWeight: 600, marginRight: '8px'
                }}>
                  {user?.role?.toUpperCase()}
                </span>
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastUpdated && (
            <span style={{ fontSize: '12px', color: '#aaa' }}>
              Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => setRefreshKey(prev => prev + 1)}
            disabled={loading}
            style={{
              background: loading ? '#f5f5f5' : `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DARK})`,
              color: loading ? '#aaa' : 'white',
              border: 'none', padding: '10px 20px', borderRadius: '10px',
              cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600,
              fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
              transition: 'all 0.2s', boxShadow: loading ? 'none' : '0 4px 12px rgba(245,124,0,0.3)'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}>
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* ── STAT CARDS ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px', marginBottom: '32px'
      }}>
        {cards.map((c, i) => (
          <div
            key={c.label}
            onClick={() => navigate(c.path)}
            style={{
              background: 'white', padding: '24px', borderRadius: '16px',
              cursor: 'pointer', transition: 'all 0.2s',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0', position: 'relative', overflow: 'hidden'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(245,124,0,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
          >
            {/* accent line */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${ORANGE}, ${ORANGE_DARK})` }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: ORANGE_LIGHT, color: ORANGE,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {c.icon}
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '20px',
                background: c.trendUp ? '#e8f5e9' : '#ffebee',
                color: c.trendUp ? '#2e7d32' : '#c62828'
              }}>
                {c.trend}
              </span>
            </div>

            <div style={{ fontSize: '28px', fontWeight: 800, color: '#1a1a1a', marginBottom: '4px', letterSpacing: '-0.5px' }}>
              {loading ? (
                <div style={{ width: '80px', height: '28px', background: '#f0f0f0', borderRadius: '6px', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ) : c.value}
            </div>
            <div style={{ fontSize: '13px', color: '#888', fontWeight: 500 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── RECENT SCHEDULES ── */}
      <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', overflow: 'hidden' }}>

        {/* table header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, color: '#1a1a1a', fontSize: '16px', fontWeight: 700 }}>Recent Schedules</h3>
            <p style={{ margin: '2px 0 0', color: '#aaa', fontSize: '12px' }}>Latest 5 exam schedules</p>
          </div>
          <button
            onClick={() => navigate('/schedules')}
            style={{
              background: ORANGE_LIGHT, color: ORANGE, border: 'none',
              padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
              fontWeight: 600, fontSize: '13px'
            }}
          >
            View All →
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div style={{ width: '36px', height: '36px', border: `3px solid ${ORANGE_LIGHT}`, borderTop: `3px solid ${ORANGE}`, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Loading schedules...</p>
          </div>
        ) : recentSchedules.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#bbb' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '12px', display: 'block', margin: '0 auto 12px' }}>
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <p style={{ margin: 0, fontSize: '14px' }}>No schedules found</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Exam Name', 'Type', 'Start Date', 'End Date', 'Status'].map(h => (
                  <th key={h} style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #f0f0f0' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentSchedules.map((s, i) => {
                const cfg = statusConfig[s.status] || { bg: '#f5f5f5', color: '#666', dot: '#aaa' };
                return (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/schedules/${s.id}`)}
                    style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fffaf6'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5', fontWeight: 600, color: '#1a1a1a', fontSize: '14px' }}>
                      {s.exam_name}
                    </td>
                    <td style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5', color: '#666', fontSize: '13px' }}>
                      {s.exam_type || '—'}
                    </td>
                    <td style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5', color: '#666', fontSize: '13px' }}>
                      {s.start_date}
                    </td>
                    <td style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5', color: '#666', fontSize: '13px' }}>
                      {s.end_date}
                    </td>
                    <td style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5' }}>
                      <span style={{
                        background: cfg.bg, color: cfg.color,
                        padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                        display: 'inline-flex', alignItems: 'center', gap: '5px'
                      }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
                        {s.status?.charAt(0).toUpperCase() + s.status?.slice(1)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
      `}</style>
    </div>
  );
};

export default Dashboard;